import { Modal as M } from "antd";
import { Form } from "sunday-component/node_modules/antd";
import { useTranslation } from "next-i18next";
import styled from "styled-components";
import { Title as T } from "sunday-component";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { down } from "styled-breakpoints";
import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { color } from "../../styles/globals";
import { EntryFormInputType, InitialValuesType } from "../../types/entryForm";
import { INPUT_CODE } from "../../constants/input";
import { ActiveInputCodesType } from "../../types/productCategory";
import { ENTRY_FORM_CODE } from "../../constants/entryForm";
import { validateValueByActiveInputCode } from "../../utils/validationRules";
import { formatDateText } from "../../utils/date";
import { DATE_INPUT_TEXT_FORMAT } from "../../constants/format";
import Button from "../Common/Button";

const HealthBasicEntryForm = dynamic(() => import("./HealthBasicEntryForm"), {
  ssr: false,
  loading: () => <div />,
});
const Title = styled(T)`
  color: ${color.charcoal10};
`;

const Modal = styled(M)`
  top: 0;
  margin: 0px;
  padding: 0px;
  max-width: 100vw;
  .ant-modal-header {
    border-radius: 16px 16px 0px 0px;
  }

  .ant-modal-body {
    padding: 0px;
  }
  .ant-modal-footer {
    padding: 16px;
  }
  .ant-modal-content {
    border-radius: 16px;
  }
  .ant-scrolling-effect {
    width: 0px;
  }
  ${down("md")} {
    .ant-modal,
    .ant-modal-content {
      height: 100vh;
      max-width: 100vw;
      margin: 0px;
      top: 0;
      padding: 0px;
    }
    .ant-modal-body {
      height: calc(100vh - 173px);
      width: 100vw;
      overflow: auto;
    }
    .ant-modal-footer {
      padding: 16px 16px 32px 16px;
      position: fixed;
      bottom: 0;
      width: 100%;
      background: white;
    }
    .ant-modal-content {
      overflow: auto;
      border-radius: 0px;
    }
  }
`;

export type EntryFormModalProp = {
  entryFormCode: string;
  activeInputCodes: ActiveInputCodesType[];
  visible: boolean;
  setVisible: (value: boolean) => void;
  initialValues?: InitialValuesType;
  titleText?: JSX.Element;
  headerText?: string;
  buttonText?: string;
  isInitialValuesValidate?: boolean;
  onSubmit: (value: any) => void;
  leadEnabled?: boolean;
  leadDefaultSelected?: boolean;
};

const EntryFormModal = ({
  entryFormCode,
  activeInputCodes = [],
  visible,
  setVisible = () => {},
  initialValues,
  titleText,
  headerText,
  buttonText,
  isInitialValuesValidate = false,
  onSubmit,
  leadEnabled = false,
  leadDefaultSelected = false,
}: EntryFormModalProp) => {
  const { t } = useTranslation("plan-page");
  const { lg } = useBreakpoint();
  const [form] = Form.useForm();

  // loading state
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // submit state
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  useEffect(() => {
    if (isInitialValuesValidate) {
      const inputValidationResult = validateValueByActiveInputCode(
        activeInputCodes,
        initialValues
      );

      // show entry form when there're error field
      const inputErrors = inputValidationResult.filter(
        (validationResult) => validationResult.isError
      );

      if (inputErrors.length > 0) {
        setVisible(true);
        setIsSubmitted(true);
      } else {
        // map active input codes to input field name.
        const inputValue: Partial<EntryFormInputType> = {
          effectiveDate: null,
          insuredDateOfBirth: null,
          insuredGender: null,
        };
        activeInputCodes.forEach((activeInputCode) => {
          switch (activeInputCode.inputCode) {
            case INPUT_CODE.EFFECTIVE_DATE:
              inputValue.effectiveDate = initialValues?.effectiveDate?.isValid()
                ? initialValues?.effectiveDate?.format("YYYY-MM-DD")
                : null;
              break;
            case INPUT_CODE.INSURED_DATE_OF_BIRTH:
              inputValue.insuredDateOfBirth =
                initialValues?.insuredDateOfBirth?.isValid()
                  ? initialValues?.insuredDateOfBirth?.format("YYYY-MM-DD")
                  : null;
              break;
            case INPUT_CODE.INSURED_GENDER:
              inputValue.insuredGender = initialValues?.insuredGender || null;
              break;
          }
        });

        onSubmit(inputValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const styles = {
    form: {
      paddingBottom: "16px",
    },
  };

  const handleButtonSubmit = () => {
    setIsLoading(true);
    setIsSubmitted(true);
    form.submit();
  };

  const onFormFinishSuccess = () => {
    // map active input codes to input field name.
    const inputValue: InitialValuesType = {
      effectiveDate: form.getFieldValue(INPUT_CODE.EFFECTIVE_DATE) || null,
      insuredDateOfBirth:
        form.getFieldValue(INPUT_CODE.INSURED_DATE_OF_BIRTH) || null,
      insuredGender: form.getFieldValue(INPUT_CODE.INSURED_GENDER) || null,
    };

    const inputValidationResult = validateValueByActiveInputCode(
      activeInputCodes,
      inputValue
    );

    // show entry form when there're error field
    const inputErrors = inputValidationResult.filter(
      (validationResult) => validationResult.isError
    );

    if (inputErrors.length === 0) {
      const entryFormInputValue: EntryFormInputType = {
        effectiveDate: null,
        insuredDateOfBirth: null,
        insuredGender: null,
        leadRequest: form.getFieldValue(INPUT_CODE.LEAD_REQUEST) || false,
        fullName: form.getFieldValue(INPUT_CODE.FULL_NAME) || null,
        mobileNumber: form.getFieldValue(INPUT_CODE.MOBILE_NUMBER) || null,
      };

      activeInputCodes.forEach((activeInputCode) => {
        switch (activeInputCode.inputCode) {
          case INPUT_CODE.EFFECTIVE_DATE:
            entryFormInputValue.effectiveDate = form
              .getFieldValue(INPUT_CODE.EFFECTIVE_DATE)
              ?.isValid()
              ? formatDateText(
                  form.getFieldValue(INPUT_CODE.EFFECTIVE_DATE),
                  DATE_INPUT_TEXT_FORMAT
                )
              : null;
            break;
          case INPUT_CODE.INSURED_DATE_OF_BIRTH:
            entryFormInputValue.insuredDateOfBirth = form
              .getFieldValue(INPUT_CODE.INSURED_DATE_OF_BIRTH)
              ?.isValid()
              ? formatDateText(
                  form.getFieldValue(INPUT_CODE.INSURED_DATE_OF_BIRTH),
                  DATE_INPUT_TEXT_FORMAT
                )
              : null;
            break;
          case INPUT_CODE.INSURED_GENDER:
            entryFormInputValue.insuredGender =
              form.getFieldValue(INPUT_CODE.INSURED_GENDER) || null;
            break;
        }
      });

      setVisible(false);
      onSubmit(entryFormInputValue);
    }

    setIsLoading(false);
    setIsSubmitted(false);
  };

  const onFormFinishFailed = () => {
    setIsLoading(false);
    setIsSubmitted(false);
  };

  return (
    <Modal
      title={
        <Title level={5} style={{ textAlign: "center" }}>
          {headerText || t("coverageDetail.title")}
        </Title>
      }
      footer={
        <Button
          htmlType="submit"
          data-cy="entry-form.submit-button"
          loading={isLoading}
          onClick={handleButtonSubmit}
          block
        >
          {buttonText || t("coverageDetail.formButton")}
        </Button>
      }
      visible={visible}
      onCancel={() => {
        setVisible(false);
      }}
      width={lg ? 444 : "100vw"}
      maskClosable={false}
      centered={!!lg}
    >
      <div style={styles.form}>
        {titleText && <>{titleText}</>}
        {entryFormCode === ENTRY_FORM_CODE.HEALTH_BASIC_ENTRY_FORM && (
          <HealthBasicEntryForm
            form={form}
            activeInputCodes={activeInputCodes}
            initialValues={initialValues}
            onFormFinishSuccess={onFormFinishSuccess}
            onFormFinishFailed={onFormFinishFailed}
            isLoading={isLoading}
            isSubmitted={isSubmitted}
            leadEnabled={leadEnabled}
            leadDefaultSelected={leadDefaultSelected}
          />
        )}
      </div>
    </Modal>
  );
};

export default EntryFormModal;
